/**
 * Shared data hooks for the Call Centre console: lookups (one call for every lookup), ministries, settings,
 * callers, campaign options and journey steps. Each hook memoises the select options the pages need.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCallCentreAdminService, useCallCentreLookupsService, type CallCentreLookupItem } from '@actschurch/ccms-sdk';
import { callCentreKeys } from './callCentreUtils';

export interface SelectOption {
  value: string;
  label: string;
}

/** Lookup items as select options (active ones, plus the current value even when it was switched off). */
export function lookupOptions(items: CallCentreLookupItem[] | undefined, keep?: number | null): SelectOption[] {
  return (items ?? [])
    .filter((i) => i.isActive || i.id === keep)
    .map((i) => ({ value: String(i.id), label: i.name }));
}

export function useCallCentreLookups() {
  const lookups = useCallCentreLookupsService();
  return useQuery({
    queryKey: callCentreKeys.lookups(),
    queryFn: () => lookups.getLookups(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useCallCentreMinistries() {
  const lookups = useCallCentreLookupsService();
  const query = useQuery({
    queryKey: callCentreKeys.ministries(),
    queryFn: () => lookups.getMinistries(),
    staleTime: 10 * 60_000,
  });
  const options = useMemo<SelectOption[]>(
    () => (query.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)).map((m) => ({ value: String(m.ministryID), label: m.name })),
    [query.data]
  );
  return { ...query, options };
}

export function useCallCentreSettings(enabled = true) {
  const admin = useCallCentreAdminService();
  return useQuery({
    queryKey: callCentreKeys.settings(),
    queryFn: () => admin.getSettings(),
    staleTime: 60_000,
    enabled,
  });
}

/** Everyone set up as a caller (and the call centre roles they hold). */
export function useCallCentreCallers(enabled = true) {
  const admin = useCallCentreAdminService();
  const query = useQuery({
    queryKey: callCentreKeys.callers(),
    queryFn: () => admin.getCallers(),
    staleTime: 60_000,
    enabled,
  });
  const options = useMemo<SelectOption[]>(
    () =>
      (query.data ?? [])
        .slice()
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((c) => ({ value: c.userId, label: c.email ? `${c.displayName} (${c.email})` : c.displayName })),
    [query.data]
  );
  return { ...query, callers: query.data ?? [], options };
}

/** Caller teams (Stage 5), active ones unless asked for, sorted by name, plus select options. */
export function useCallCentreTeams(includeInactive = false, enabled = true) {
  const admin = useCallCentreAdminService();
  const query = useQuery({
    queryKey: [...callCentreKeys.teams(), includeInactive] as const,
    queryFn: () => admin.listTeams(includeInactive),
    staleTime: 60_000,
    enabled,
  });
  const teams = useMemo(() => (query.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)), [query.data]);
  const options = useMemo<SelectOption[]>(
    () => teams.map((t) => ({ value: String(t.teamID), label: `${t.name}${t.isActive ? '' : ' (switched off)'}` })),
    [teams]
  );
  return { ...query, teams, options };
}

/** Campaigns as select options (newest first, archived left out unless asked for). */
export function useCampaignOptions(includeArchived = false) {
  const admin = useCallCentreAdminService();
  const q = { includeArchived };
  const query = useQuery({
    queryKey: callCentreKeys.campaigns(q),
    queryFn: () => admin.listCampaigns(q),
    staleTime: 60_000,
  });
  const options = useMemo<SelectOption[]>(
    () =>
      (query.data ?? [])
        .slice()
        .sort((a, b) => b.createdDateTime - a.createdDateTime)
        .map((c) => ({ value: String(c.campaignID), label: c.name })),
    [query.data]
  );
  return { ...query, options };
}

export function useJourneySteps() {
  const admin = useCallCentreAdminService();
  const query = useQuery({
    queryKey: callCentreKeys.journeySteps(),
    queryFn: () => admin.getJourneySteps(),
    staleTime: 5 * 60_000,
  });
  const steps = useMemo(() => (query.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code)), [query.data]);
  return { ...query, steps };
}
